import React, { useState, useCallback, useRef } from "react";
import { GoogleMap, Marker, Circle, useLoadScript, StandaloneSearchBox } from "@react-google-maps/api";
import { Button, NumberInput, Switch, TextInput, Textarea, Group, Loader } from "@mantine/core";

function createUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const randomValue = Math.floor(Math.random() * 16);
    const uuidValue = char === "x" ? randomValue : (randomValue & 0x3) | 0x8;
    return uuidValue.toString(16);
  });
}

const mapContainerStyle = {
  width: "100%",
  height: "350px",
};

const defaultCenter = { lat: -25.746111, lng: 28.188056 };

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ["places"];

export interface GeofenceValue {
  identifier: string;
  latitude: number;
  longitude: number;
  radius: number;
  notifyOnEntry: boolean;
  notifyOnExit: boolean;
  extras: {
    note: string;
    landmark: string;
  };
}

export interface GeofenceMapProps {
  value?: GeofenceValue | null;
  onChange: (newValue: GeofenceValue) => void;
  onCancel: () => void;
  currentCount?: number;
}

export interface MarkerPosition {
  lat: number;
  lng: number;
}

export function GeofenceMap({
  value,
  onChange,
  onCancel,
  currentCount = 0,
}: GeofenceMapProps) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [marker, setMarker] = useState<MarkerPosition | null>(
    value ? { lat: value.latitude, lng: value.longitude } : null
  );
  const [radius, setRadius] = useState<number>(value?.radius || 1000);
  const [notifyOnEntry, setNotifyOnEntry] = useState(value?.notifyOnEntry ?? true);
  const [notifyOnExit, setNotifyOnExit] = useState(value?.notifyOnExit ?? true);
  const [note, setNote] = useState(value?.extras?.note || "");
  const [landmark, setLandmark] = useState(value?.extras?.landmark || "");
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  }, []);

  const onPlacesChanged = () => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const loc = places[0].geometry?.location;
      if (loc) {
        setMarker({ lat: loc.lat(), lng: loc.lng() });
      }
    }
  };

  // Helper to fetch address from lat/lng
  const fetchLandmark = async (lat: number, lng: number) => {
    if (!window.google || !window.google.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        // Use the first result's short address
        setLandmark(results[0].formatted_address || "");
      } else {
        setLandmark("");
      }
    });
  };

  // Update landmark when marker changes
  React.useEffect(() => {
    if (marker) {
      fetchLandmark(marker.lat, marker.lng);
    }
  }, [marker]);

  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <Loader />;

  return (
    <div>
      <StandaloneSearchBox onLoad={ref => (searchBoxRef.current = ref)} onPlacesChanged={onPlacesChanged}>
        <TextInput placeholder="Search for address..." style={{ marginBottom: 8 }} />
      </StandaloneSearchBox>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={marker || defaultCenter}
        zoom={marker ? 14 : 8}
        onClick={onMapClick}
      >
        {marker && (
          <Marker
            position={marker}
            draggable={true}
            onDragEnd={e => {
              if (e.latLng) {
                setMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() });
              }
            }}
          />
        )}
        {marker && <Circle center={marker} radius={radius} options={{ fillColor: '#1976d2', fillOpacity: 0.2, strokeColor: '#1976d2' }} />}
      </GoogleMap>
      <Group direction="column" grow mt="md" spacing={8}>
        <NumberInput
          label="Radius (meters)"
          value={radius}
          onChange={(val) => setRadius(val || 10)}
          min={10}
          max={10000}
          required
        />
        <Switch label="Notify on Entry" checked={notifyOnEntry} onChange={e => setNotifyOnEntry(e.currentTarget.checked)} />
        <Switch label="Notify on Exit" checked={notifyOnExit} onChange={e => setNotifyOnExit(e.currentTarget.checked)} />
        <TextInput label="Landmark (auto-filled)" value={landmark} readOnly />
        <Textarea label="Note" value={note} onChange={e => setNote(e.target.value)} autosize minRows={2} />
      </Group>
      <Group position="right" mt="md">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => {
            if (!marker || currentCount >= 20) return;
            onChange({
              identifier: createUuid(),
              radius,
              latitude: marker.lat,
              longitude: marker.lng,
              notifyOnEntry,
              notifyOnExit,
              extras: { note, landmark },
            });
          }}
          disabled={!marker || currentCount >= 20}
        >
          Save Checkpoint
        </Button>
      </Group>
      {currentCount >= 20 && (
        <div style={{ color: 'red', marginTop: 8 }}>
          Maximum of 20 checkpoints reached.
        </div>
      )}
    </div>
  );
}

